import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, TrendingUp, ShoppingCart, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { addDays, format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTenant } from "@/contexts/TenantContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Dashboard() {
  const { tenantId } = useTenant();
  const [quickPeriod, setQuickPeriod] = useState<
    "today" | "yesterday" | "beforeYesterday" | "week" | "month"
  >("today");

  const dateRange = useMemo(() => {
    const now = new Date();
    if (quickPeriod === "today") {
      return { from: startOfDay(now), to: endOfDay(now) };
    }
    if (quickPeriod === "yesterday") {
      const y = subDays(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    if (quickPeriod === "beforeYesterday") {
      const d = subDays(now, 2);
      return { from: startOfDay(d), to: endOfDay(d) };
    }
    if (quickPeriod === "week") {
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
    }
    return { from: startOfMonth(now), to: endOfMonth(now) };
  }, [quickPeriod]);

  // Fetch transactions
  const { data: transactions, isLoading } = useQuery({
    queryKey: ["dashboard-transactions", tenantId, dateRange],
    queryFn: async () => {
      if (!tenantId) return [] as any[];
      const { data, error } = await supabase
        .from("transactions")
        .select(`
          id, created_at, tipo, status, valor_total, valor_liquido, payment_method_id, bank_account_id, tenant_id,
          transaction_items (id, product_id, quantidade, preco_unitario, total, products (preco_custo, product_categories (id, nome))),
          payment_methods:payment_method_id (nome),
          bank_accounts:bank_account_id (nome)
        `)
        .gte("created_at", dateRange.from.toISOString())
        .lte("created_at", dateRange.to.toISOString())
        .eq("status", "paga")
        .eq("tenant_id", tenantId);
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch transaction_payments (confirmados) no período
  const { data: txPays = [] } = useQuery({
    queryKey: ["dashboard-tx-pays", tenantId, dateRange],
    queryFn: async () => {
      if (!tenantId) return [] as any[];
      const { data, error } = await supabase
        .from('transaction_payments')
        .select(`
          id, valor, status, bank_account_id, payment_method_id, created_at, confirmed_at,
          transactions!inner(id, created_at, tenant_id, tipo)
        `)
        .eq('transactions.tenant_id', tenantId)
        .gte('transactions.created_at', dateRange.from.toISOString())
        .lte('transactions.created_at', dateRange.to.toISOString())
        .eq('status', 'confirmado');
      if (error) throw error;
      return data || [];
    },
  });

  // Nomes dos métodos de pagamento
  const { data: paymentMethods } = useQuery({
    queryKey: ["dashboard-payment-methods"],
    queryFn: async () => {
      const { data, error } = await supabase.from('payment_methods').select('id, nome');
      if (error) throw error;
      return data as Array<{ id: string; nome: string }>;
    },
  });
  const pmName = useMemo(() => {
    const m = new Map<string, string>();
    (paymentMethods || []).forEach((p) => m.set(p.id, p.nome));
    return m;
  }, [paymentMethods]);

  // Fetch bank accounts to ensure names are resolved even if nested join is restricted by RLS
  const { data: bankAccounts } = useQuery({
    queryKey: ["dashboard-bank-accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as Array<{ id: string; nome: string }>; 
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, nome, tenant_id, status")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo");
      if (error) throw error;
      return (data || []).map((a: any) => ({ id: a.id, nome: a.nome }));
    },
  });

  const accountsMap = useMemo(() => {
    const m = new Map<string, string>();
    (bankAccounts || []).forEach((a) => m.set(a.id, a.nome));
    return m;
  }, [bankAccounts]);

  // Mapear método de pagamento -> conta bancária
  const { data: bankAccountMethods } = useQuery({
    queryKey: ["dashboard-bank-account-methods", tenantId],
    queryFn: async () => {
      // sem tenant_id direto; filtraremos no client pelas contas do tenant
      const { data, error } = await supabase
        .from("bank_accounts_methods")
        .select("bank_account_id, payment_method_id");
      if (error) throw error;
      return data as Array<{ bank_account_id: string; payment_method_id: string }>;
    },
  });

  const methodToAccountId = useMemo(() => {
    const m = new Map<string, string>();
    (bankAccountMethods || []).forEach((bam) => {
      if (accountsMap.has(bam.bank_account_id)) {
        m.set(bam.payment_method_id, bam.bank_account_id);
      }
    });
    return m;
  }, [bankAccountMethods, accountsMap]);

  // Calculate KPIs
  const faturamento = transactions?.filter(t => t.tipo === "entrada")
    .reduce((sum, t) => sum + Number(t.valor_liquido || 0), 0) || 0;
  
  const saidas = transactions?.filter(t => t.tipo === "saida")
    .reduce((sum, t) => sum + Number(t.valor_liquido || 0), 0) || 0;
  
  const vendas = transactions?.filter(t => t.tipo === "entrada").length || 0;
  
  const ticketMedio = vendas > 0 ? faturamento / vendas : 0;

  // Cálculo de custo e lucro bruto a partir dos itens vendidos
  const custoTotal = useMemo(() => {
    const entradas = (transactions || []).filter((t: any) => t.tipo === "entrada");
    let total = 0;
    entradas.forEach((t: any) => {
      (t.transaction_items || []).forEach((it: any) => {
        const custo = Number(it?.products?.preco_custo || 0);
        const qtd = Number(it?.quantidade || 0);
        total += custo * qtd;
      });
    });
    return total;
  }, [transactions]);

  const lucroBruto = Math.max(0, faturamento - custoTotal);

  // Entradas/Saídas por conta bancária
  const { entradasPorConta, saidasPorConta } = useMemo(() => {
    const eMap = new Map<string, number>();
    const sMap = new Map<string, number>();
    (transactions || []).forEach((t: any) => {
      if (t.tipo === 'entrada') {
        // Somar por pagamentos confirmados desta transação
        const pays = (txPays || []).filter((p: any) => p.transactions?.id === t.id);
        if (pays.length > 0) {
          pays.forEach((p: any) => {
            const methodId = p?.payment_method_id ? String(p.payment_method_id) : undefined;
            let contaId: string | undefined = p?.bank_account_id || t?.bank_account_id || undefined;
            if (!contaId && methodId) {
              const mapped = methodToAccountId.get(methodId);
              if (mapped) contaId = mapped;
            }
            const contaNome = contaId ? accountsMap.get(contaId) : undefined;
            const conta = contaNome || t?.bank_accounts?.nome || 'Sem conta';
            const valor = Number(p?.valor ?? 0);
            eMap.set(conta, (eMap.get(conta) || 0) + valor);
          });
        } else {
          // Fallback
          let contaId: string | undefined = t?.bank_account_id || undefined;
          const contaNome = contaId ? accountsMap.get(contaId) : undefined;
          const conta = contaNome || t?.bank_accounts?.nome || 'Sem conta';
          const valor = Number(t?.valor_liquido || 0);
          eMap.set(conta, (eMap.get(conta) || 0) + valor);
        }
      } else if (t.tipo === 'saida') {
        // Saídas: somar por conta a partir de bank_account_id ou map por método
        let contaId: string | undefined = t?.bank_account_id || undefined;
        if (!contaId && t?.payment_method_id) {
          const mapped = methodToAccountId.get(String(t.payment_method_id));
          if (mapped) contaId = mapped;
        }
        const contaNome = contaId ? accountsMap.get(contaId) : undefined;
        const conta = contaNome || t?.bank_accounts?.nome || 'Sem conta';
        const valor = Number(t?.valor_liquido || 0);
        sMap.set(conta, (sMap.get(conta) || 0) + valor);
      }
    });
    return { entradasPorConta: eMap, saidasPorConta: sMap };
  }, [transactions, txPays, accountsMap, methodToAccountId]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral do seu negócio</p>
        </div>
        <div className="text-center py-8">Carregando dados...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-cy="dashboard-container">
      <div data-cy="dashboard-header" className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" data-cy="dashboard-title">Visão Geral do Negócio</h1>
          <p className="text-sm md:text-base text-muted-foreground" data-cy="dashboard-subtitle">
            Principais métricas do seu negócio
            <strong className="text-primary ml-1">
               {quickPeriod === 'today' && 'Hoje'}{quickPeriod === 'yesterday' && 'Ontem'}{quickPeriod === 'beforeYesterday' && 'Antes de Ontem'}{quickPeriod === 'week' && 'Esta Semana'}{quickPeriod === 'month' && 'Este Mês'}
            </strong>
          </p>
        </div>
        <Tabs value={quickPeriod} onValueChange={(v) => setQuickPeriod(v as any)} className="w-full md:w-auto">
          <TabsList>
            <TabsTrigger value="today">Hoje</TabsTrigger>
            <TabsTrigger value="yesterday">Ontem</TabsTrigger>
            <TabsTrigger value="beforeYesterday">Antes de Ontem</TabsTrigger>
            <TabsTrigger value="week">Semana</TabsTrigger>
            <TabsTrigger value="month">Mês</TabsTrigger>
          </TabsList>
          <TabsContent value={quickPeriod} />
        </Tabs>
      </div>

      {/* Informações do Período - full width */}
      <Card data-cy="periodo-top-card" className="p-0">
        <CardContent className="pb-3">
          <div className="grid gap-4 pt-3 grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">Período</p>
              <p className="font-medium" data-cy="periodo-data">
                {['week','month'].includes(quickPeriod)
                  ? `${format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} - ${format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}`
                  : `${format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })}`}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Transações</p>
              <p className="font-bold text-2xl" data-cy="total-transacoes-top">{transactions?.length || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Vendas Realizadas</p>
              <p className="font-bold text-2xl" data-cy="vendas-realizadas-top">{vendas}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ticket Médio</p>
              <p className="font-bold text-2xl" data-cy="ticket-medio-top">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ticketMedio)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" data-cy="kpi-cards">
        <Card data-cy="faturamento-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Faturamento</CardTitle>
            <DollarSign className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-success" data-cy="faturamento-value">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(faturamento)}
            </div>
            <p className="text-xs text-muted-foreground">No período selecionado</p>
          </CardContent>
        </Card>

        <Card data-cy="saidas-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Saídas</CardTitle>
            <TrendingUp className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-destructive" data-cy="saidas-value">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(saidas)}
            </div>
            <p className="text-xs text-muted-foreground">No período selecionado</p>
          </CardContent>
        </Card>

        <Card data-cy="vendas-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Vendas</CardTitle>
            <ShoppingCart className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold" data-cy="vendas-value">{vendas}</div>
            <p className="text-xs text-muted-foreground">Transações no período</p>
          </CardContent>
        </Card>

        <Card data-cy="ticket-medio-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Ticket Médio</CardTitle>
            <Package className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold" data-cy="ticket-medio-value">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ticketMedio)}
            </div>
            <p className="text-xs text-muted-foreground">Média por venda</p>
          </CardContent>
        </Card>
      </div>

      {/* Resumo + por conta bancária */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3" data-cy="resumo-e-contas">
        <Card data-cy="resumo-financeiro-card">
          <CardHeader>
            <CardTitle className="text-base md:text-xl">Resumo Financeiro</CardTitle>
          </CardHeader>
          <CardContent className="text-sm md:text-base">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total de Entradas</span>
                <span className="font-bold text-success" data-cy="total-entradas">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(faturamento)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total de Saídas</span>
                <span className="font-bold text-destructive" data-cy="total-saidas">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(saidas)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-sm font-medium">Saldo do Período</span>
                <span className={`font-bold ${(faturamento - saidas) >= 0 ? 'text-success' : 'text-destructive'}`} data-cy="saldo-periodo">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(faturamento - saidas)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-cy="entradas-por-conta-card">
          <CardHeader>
            <CardTitle className="text-base md:text-xl">Entradas por Conta</CardTitle>
          </CardHeader>
          <CardContent className="text-sm md:text-base">
            <div className="space-y-2">
              {Array.from(entradasPorConta.entries()).length === 0 && (
                <p className="text-muted-foreground text-sm">Sem dados</p>
              )}
              {Array.from(entradasPorConta.entries()).map(([conta, valor]) => (
                <div key={`e-${conta}`} className="flex items-center justify-between">
                  <span className="truncate pr-2">{conta}</span>
                  <span className="font-semibold text-success">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card data-cy="saidas-por-conta-card">
          <CardHeader>
            <CardTitle className="text-base md:text-xl">Saídas por Conta</CardTitle>
          </CardHeader>
          <CardContent className="text-sm md:text-base">
            <div className="space-y-2">
              {Array.from(saidasPorConta.entries()).length === 0 && (
                <p className="text-muted-foreground text-sm">Sem dados</p>
              )}
              {Array.from(saidasPorConta.entries()).map(([conta, valor]) => (
                <div key={`s-${conta}`} className="flex items-center justify-between">
                  <span className="truncate pr-2">{conta}</span>
                  <span className="font-semibold text-destructive">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Séries temporais e comparativos */}
      <TemporalAndBreakdownCharts 
        transactions={transactions || []} 
        dateRange={dateRange} 
        txPays={txPays || []} 
        pmName={pmName}
        accountsMap={accountsMap}
      />
    </div>
  );
}

// Componentes auxiliares (mantidos no mesmo arquivo para simplicidade)
function TemporalAndBreakdownCharts({ 
  transactions, 
  dateRange, 
  txPays = [], 
  pmName,
  accountsMap 
}: { 
  transactions: any[]; 
  dateRange: { from: Date; to: Date }; 
  txPays?: any[]; 
  pmName?: Map<string, string>;
  accountsMap?: Map<string, string>;
}) {
  const [drillDownData, setDrillDownData] = useState<{
    title: string;
    transactions: any[];
  } | null>(null);
  // Agrupar por dia
  const formatKey = (d: Date) => format(d, "dd/MM", { locale: ptBR });

  const days: string[] = [];
  for (let d = new Date(dateRange.from); d <= dateRange.to; d = addDays(d, 1)) {
    days.push(formatKey(d));
  }

  const baseMap = new Map(
    days.map((k) => [k, { receita: 0, custo: 0, lucro: 0, entradas: 0, saidas: 0 }])
  );

  (transactions || []).forEach((t: any) => {
    const key = formatKey(new Date(t.created_at));
    if (!baseMap.has(key)) return;
    if (t.tipo === "entrada") {
      const receita = Number(t.valor_liquido || 0);
      const custo = (t.transaction_items || []).reduce((acc: number, it: any) => acc + Number(it?.products?.preco_custo || 0) * Number(it?.quantidade || 0), 0);
      const prev = baseMap.get(key)!;
      prev.receita += receita;
      prev.custo += custo;
      prev.lucro += Math.max(0, receita - custo);
      prev.entradas += receita;
    } else if (t.tipo === "saida") {
      const prev = baseMap.get(key)!;
      prev.saidas += Number(t.valor_liquido || 0);
    }
  });

  const seriesData = Array.from(baseMap.entries()).map(([dia, v]) => ({ dia, ...v }));

  // Pizza por método de pagamento (com base em transaction_payments confirmados)
  const porMetodo = new Map<string, number>();
  (txPays || []).forEach((p: any) => {
    const id = p?.payment_method_id as string | null;
    const nome = id ? ((pmName?.get(id)) || id) : 'Indefinido';
    const valor = Number(p?.valor || 0);
    porMetodo.set(nome, (porMetodo.get(nome) || 0) + valor);
  });
  const metodoData = Array.from(porMetodo.entries()).map(([metodo, valor]) => ({ metodo, valor }));

  // Pizza por categoria de produto
  const porCategoria = new Map<string, number>();
  (transactions || []).forEach((t: any) => {
    if (t.tipo !== "entrada") return;
    (t.transaction_items || []).forEach((it: any) => {
      const categoriaNome = it?.products?.product_categories?.nome || "Sem categoria";
      const receitaItem = Number(it?.total || it?.preco_unitario || 0) * Number(it?.quantidade || 1);
      porCategoria.set(categoriaNome, (porCategoria.get(categoriaNome) || 0) + receitaItem);
    });
  });
  const categoriaData = Array.from(porCategoria.entries()).map(([categoria, valor]) => ({ categoria, valor }));

  const COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#ef4444", "#0ea5e9", "#8b5cf6", "#14b8a6", "#f43f5e"];

  // Função para formatar valores monetários
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Formatter para Tooltip (Recharts aceita value, name, props, index, payload)
  const tooltipFormatter = (value: any) => {
    const numValue = typeof value === 'number' ? value : Number(value) || 0;
    return formatCurrency(numValue);
  };

  // Formatter para YAxis
  const yAxisFormatter = (value: number) => {
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}k`;
    }
    return formatCurrency(value);
  };

  // Formatter para labels do PieChart
  const pieLabelFormatter = (entry: any) => {
    // O label do PieChart recebe um objeto com propriedades do entry
    const value = entry?.value !== undefined ? entry.value : (typeof entry === 'number' ? entry : 0);
    return formatCurrency(Number(value) || 0);
  };

  // Helper para parsear data do formato "dd/MM"
  const parseDateFromKey = (diaKey: string): { start: Date; end: Date } | null => {
    if (!diaKey) return null;
    const [day, month] = diaKey.split('/');
    if (!day || !month) return null;
    
    // Usar o ano do dateRange para garantir que estamos no período correto
    const year = dateRange.from.getFullYear();
    const clickedDate = new Date(year, parseInt(month) - 1, parseInt(day));
    
    // Verificar se a data está dentro do range (pode ser do ano anterior se estivermos no início do ano)
    if (clickedDate < dateRange.from) {
      clickedDate.setFullYear(year + 1);
    }
    
    return {
      start: startOfDay(clickedDate),
      end: endOfDay(clickedDate),
    };
  };

  // Handler para clique no gráfico de linha
  const handleLineChartClick = (data: any) => {
    if (!data || !data.dia) return;
    
    const clickedDateRange = parseDateFromKey(data.dia);
    if (!clickedDateRange) return;

    // Filtrar transações do dia clicado (entradas)
    const filtered = transactions.filter((t: any) => {
      const tDate = new Date(t.created_at);
      return tDate >= clickedDateRange.start && tDate <= clickedDateRange.end && t.tipo === 'entrada';
    });

    setDrillDownData({
      title: `Transações do dia ${data.dia}`,
      transactions: filtered,
    });
  };

  // Handler para clique no gráfico de barras
  const handleBarChartClick = (data: any, dataKey: string) => {
    if (!data || !data.dia) return;
    
    const clickedDateRange = parseDateFromKey(data.dia);
    if (!clickedDateRange) return;

    // Filtrar por tipo baseado no dataKey
    const tipo = dataKey === 'entradas' ? 'entrada' : 'saida';
    const filtered = transactions.filter((t: any) => {
      const tDate = new Date(t.created_at);
      return tDate >= clickedDateRange.start && tDate <= clickedDateRange.end && t.tipo === tipo;
    });

    setDrillDownData({
      title: `${dataKey === 'entradas' ? 'Entradas' : 'Saídas'} do dia ${data.dia}`,
      transactions: filtered,
    });
  };

  // Handler para clique no gráfico de pizza (método de pagamento)
  const handlePieChartMethodClick = (data: any) => {
    if (!data || !data.metodo) return;

    // Filtrar transaction_payments pelo método (mesma lógica do gráfico)
    const filteredPays = txPays.filter((p: any) => {
      const id = p?.payment_method_id as string | null;
      const nome = id ? ((pmName?.get(id)) || id) : 'Indefinido';
      return nome === data.metodo;
    });

    // Criar um mapa de transaction_id -> soma dos valores dos pagamentos deste método
    const transactionValueMap = new Map<string, number>();
    filteredPays.forEach((p: any) => {
      const txId = p.transactions?.id;
      if (txId) {
        const currentValue = transactionValueMap.get(txId) || 0;
        transactionValueMap.set(txId, currentValue + Number(p?.valor || 0));
      }
    });

    // Buscar as transações relacionadas e adicionar o valor calculado
    const transactionIds = Array.from(transactionValueMap.keys());
    const filtered = transactions
      .filter((t: any) => transactionIds.includes(t.id))
      .map((t: any) => ({
        ...t,
        // Adicionar um campo calculado com o valor específico deste método
        valor_metodo: transactionValueMap.get(t.id) || 0,
      }));

    setDrillDownData({
      title: `Transações - Método: ${data.metodo} (Total: ${formatCurrency(Array.from(transactionValueMap.values()).reduce((sum, val) => sum + val, 0))})`,
      transactions: filtered,
    });
  };

  // Handler para clique no gráfico de pizza (categoria)
  const handlePieChartCategoryClick = (data: any) => {
    if (!data || !data.categoria) return;

    // Filtrar transações que têm itens com produtos da categoria e calcular valor da categoria
    let totalCategoria = 0;
    const filtered = transactions
      .filter((t: any) => {
        if (t.tipo !== 'entrada') return false;
        return (t.transaction_items || []).some((it: any) => {
          const categoriaNome = it?.products?.product_categories?.nome || "Sem categoria";
          return categoriaNome === data.categoria;
        });
      })
      .map((t: any) => {
        // Calcular o valor total dos itens desta categoria nesta transação
        const valorCategoria = (t.transaction_items || []).reduce((sum: number, it: any) => {
          const categoriaNome = it?.products?.product_categories?.nome || "Sem categoria";
          if (categoriaNome === data.categoria) {
            const receitaItem = Number(it?.total || it?.preco_unitario || 0) * Number(it?.quantidade || 1);
            return sum + receitaItem;
          }
          return sum;
        }, 0);
        totalCategoria += valorCategoria;
        return {
          ...t,
          // Adicionar um campo calculado com o valor específico desta categoria
          valor_categoria: valorCategoria,
        };
      });

    setDrillDownData({
      title: `Transações - Categoria: ${data.categoria} (Total: ${formatCurrency(totalCategoria)})`,
      transactions: filtered,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-xl">Receita, Custo e Lucro (por dia)</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={seriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis tickFormatter={yAxisFormatter} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="receita" 
                stroke="#16a34a" 
                name="Receita"
                onClick={handleLineChartClick}
                style={{ cursor: 'pointer' }}
              />
              <Line 
                type="monotone" 
                dataKey="custo" 
                stroke="#ef4444" 
                name="Custo"
                onClick={handleLineChartClick}
                style={{ cursor: 'pointer' }}
              />
              <Line 
                type="monotone" 
                dataKey="lucro" 
                stroke="#2563eb" 
                name="Lucro"
                onClick={handleLineChartClick}
                style={{ cursor: 'pointer' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-xl">Entradas vs Saídas (por dia)</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={seriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis tickFormatter={yAxisFormatter} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Bar 
                dataKey="entradas" 
                fill="#16a34a" 
                name="Entradas"
                onClick={(data) => handleBarChartClick(data, 'entradas')}
                cursor="pointer"
              />
              <Bar 
                dataKey="saidas" 
                fill="#ef4444" 
                name="Saídas"
                onClick={(data) => handleBarChartClick(data, 'saidas')}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-xl">Receita por Método de Pagamento</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip formatter={tooltipFormatter} />
                <Legend />
                <Pie 
                  data={metodoData} 
                  dataKey="valor" 
                  nameKey="metodo" 
                  outerRadius={80} 
                  label={pieLabelFormatter}
                  onClick={handlePieChartMethodClick}
                >
                  {metodoData.map((_, idx) => (
                    <Cell 
                      key={`met-${idx}`} 
                      fill={COLORS[idx % COLORS.length]}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-xl">Receita por Categoria</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip formatter={tooltipFormatter} />
                <Legend />
                <Pie 
                  data={categoriaData} 
                  dataKey="valor" 
                  nameKey="categoria" 
                  outerRadius={80} 
                  label={pieLabelFormatter}
                  onClick={handlePieChartCategoryClick}
                >
                  {categoriaData.map((_, idx) => (
                    <Cell 
                      key={`cat-${idx}`} 
                      fill={COLORS[idx % COLORS.length]}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Drill-Down */}
      <Dialog open={!!drillDownData} onOpenChange={() => setDrillDownData(null)}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{drillDownData?.title || 'Detalhes'}</DialogTitle>
            <DialogDescription>
              {drillDownData?.transactions.length || 0} transação(ões) encontrada(s)
            </DialogDescription>
          </DialogHeader>
          {drillDownData && drillDownData.transactions.length > 0 ? (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Método de Pagamento</TableHead>
                    <TableHead>Conta</TableHead>
                    {drillDownData.title.includes('Método:') && (
                      <TableHead className="text-right">Valor do Método</TableHead>
                    )}
                    {drillDownData.title.includes('Categoria:') && (
                      <TableHead className="text-right">Valor da Categoria</TableHead>
                    )}
                    {!drillDownData.title.includes('Método:') && !drillDownData.title.includes('Categoria:') && (
                      <>
                        <TableHead className="text-right">Valor Total</TableHead>
                        <TableHead className="text-right">Valor Líquido</TableHead>
                      </>
                    )}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.transactions.map((trans: any) => (
                    <TableRow key={trans.id}>
                      <TableCell>
                        {format(new Date(trans.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </TableCell>
                      <TableCell className="capitalize">{trans.tipo}</TableCell>
                      <TableCell>
                        {trans.payment_methods?.nome || trans.payment_method_id || '-'}
                      </TableCell>
                      <TableCell>
                        {trans.bank_accounts?.nome || 
                         (trans.bank_account_id && accountsMap?.get(trans.bank_account_id)) || 
                         '-'}
                      </TableCell>
                      {drillDownData.title.includes('Método:') && (
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(Number(trans.valor_metodo || 0))}
                        </TableCell>
                      )}
                      {drillDownData.title.includes('Categoria:') && (
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(Number(trans.valor_categoria || 0))}
                        </TableCell>
                      )}
                      {!drillDownData.title.includes('Método:') && !drillDownData.title.includes('Categoria:') && (
                        <>
                          <TableCell className="text-right">
                            {formatCurrency(Number(trans.valor_total || 0))}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(Number(trans.valor_liquido || 0))}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="capitalize">{trans.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma transação encontrada
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
